import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import CNPJMaskInput from '../masks/CNPJMaskInput';
import TelefoneMaskInput from '../masks/TelefoneMaskInput';
import CelularMaskInput from '../masks/CelularMaskInput';
import CEPMaskInput from '../masks/CEPMaskInput';
import { unformatCNPJ, formatCNPJ, isValidCNPJ } from '../../utils/cnpjUtils';
import { unformatTelefone, formatTelefone } from '../../utils/telefoneUtils';
import { unformatCelular, formatCelular } from '../../utils/celularUtils';
import { unformatCEP, formatCEP } from '../../utils/cepUtils';

const ClienteModal = ({ isOpen, cliente, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    cnpj: '',
    nome: '',
    email: '',
    telefone: '',
    fax: '',
    artigo: '',
    logrNome: '',
    logrNum: '',
    logrCompl: '',
    logrBairro: '',
    logrCep: '',
    logrCidade: '',
    logrUf: '',
  });
  const [ufs, setUfs] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isBuscandoCnpj, setIsBuscandoCnpj] = useState(false);

  const token = localStorage.getItem('token');

  // Carregar UFs ao abrir modal
  useEffect(() => {
    if (isOpen) {
      axios
        .get(`${API_BASE_URL.UF}/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setUfs(result.data);
        })
        .catch(error => {
          console.error('Erro ao obter UFs:', error);
          setError('Erro ao carregar estados');
        });
    }
  }, [isOpen, token]);

  // Carregar cidades quando UF muda
  useEffect(() => {
    if (formData.logrUf) {
      axios
        .get(`${API_BASE_URL.MUNICIPIO}/uf/${formData.logrUf}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setCidades(result.data);
        })
        .catch(error => {
          console.error('Erro ao carregar cidades:', error);
        });
    } else {
      setCidades([]);
    }
  }, [formData.logrUf, token]);

  // Atualiza formData ao abrir modal ou mudar cliente
  useEffect(() => {
    if (isOpen) {
      if (cliente) {
        setFormData({
          cnpj: cliente.cnpj || '',
          nome: cliente.nome || '',
          email: cliente.email || '',
          telefone: cliente.telefone || '',
          celular: cliente.celular || '',
          artigo: cliente.artigo || '',
          logrNome: cliente.logrNome || '',
          logrNum: cliente.logrNum || '',
          logrCompl: cliente.logrCompl || '',
          logrBairro: cliente.logrBairro || '',
          logrCep: cliente.logrCep || '',
          logrCidade: cliente.logrCidade || '',
          logrUf: cliente.logrUf || '',
        });
        setIsEditing(true);
      } else {
        // Limpa completamente o formulário para novo cliente
        setFormData({
          cnpj: '',
          nome: '',
          email: '',
          telefone: '',
          celular: '',
          artigo: '',
          logrNome: '',
          logrNum: '',
          logrCompl: '',
          logrBairro: '',
          logrCep: '',
          logrCidade: '',
          logrUf: '',
        });
        setIsEditing(false);
        setCidades([]);
      }
      setError(null);
    }
  }, [isOpen, cliente]);

  function stripMask(value) {
    return value ? value.replace(/\D/g, '') : '';
  }

  async function handleBuscarCnpj() {
    const cnpjNum = stripMask(formData.cnpj);
    
    if (cnpjNum.length !== 14) {
      setError('Digite um CNPJ válido (14 dígitos) antes de buscar.');
      return;
    }

    setIsBuscandoCnpj(true);
    setError(null);
    
    try {
      const r = await axios.get(`${API_BASE_URL.CNPJ_PROXY}/${cnpjNum}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
      
      if (d.status === 'ERROR') {
        setError(d.message || 'CNPJ não encontrado.');
        return;
      }

      // Extrair apenas dígitos do telefone para evitar problemas com tamanho da coluna
      const telefoneDigitos = (d.telefone || '').replace(/\D/g, '').substring(0, 11);

      // Preencher os dados retornados da API
      setFormData(f => ({
        ...f,
        nome: d.nome || f.nome,
        email: d.email || f.email,
        telefone: telefoneDigitos || f.telefone,
        logrNome: d.logradouro || f.logrNome,
        logrNum: d.numero || f.logrNum,
        logrCompl: d.complemento || f.logrCompl,
        logrBairro: d.bairro || f.logrBairro,
        logrCep: (d.cep || '').replace(/\D/g, '') || f.logrCep,
        logrUf: d.uf || f.logrUf,
        logrCidade: d.municipio || f.logrCidade,
      }));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError('Limite de consultas atingido. Aguarde 1 minuto e tente novamente.');
      } else if (status === 400) {
        setError('CNPJ inválido ou não encontrado.');
      } else {
        setError(`Erro ao consultar CNPJ${status ? ` (${status})` : ''}. Tente novamente em instantes.`);
      }
    } finally {
      setIsBuscandoCnpj(false);
    }
  }

  const handleChange = useCallback(event => {
    const { name, value } = event.target;
    let formattedValue = value;

    if (name === 'cnpj') {
      const unformattedValue = unformatCNPJ(value);
      formattedValue = formatCNPJ(unformattedValue);
    } else if (name === 'telefone') {
      const unformattedValue = unformatTelefone(value);
      formattedValue = formatTelefone(unformattedValue);
    } else if (name === 'celular') {
      const unformattedValue = unformatCelular(value);
      formattedValue = formatCelular(unformattedValue);
    } else if (name === 'logrCep') {
      const unformattedValue = unformatCEP(value);
      formattedValue = formatCEP(unformattedValue);
    } else {
      formattedValue =
        name === 'email' ? value.toLowerCase() : value.toUpperCase();
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue,
    }));

    // Limpar cidade quando UF muda
    if (name === 'logrUf') {
      setFormData(prev => ({
        ...prev,
        logrUf: formattedValue,
        logrCidade: '',
      }));
    }
  }, []);

  const handleSubmit = async event => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validações
      const unformattedCnpj = unformatCNPJ(formData.cnpj);

      if (!isValidCNPJ(unformattedCnpj)) {
        throw new Error('CNPJ inválido');
      }

      if (!formData.nome.trim()) {
        throw new Error('Nome é obrigatório');
      }

      // if (!formData.email.trim()) {
      //   throw new Error('E-mail é obrigatório');
      // }

      if (!formData.artigo) {
        throw new Error('Artigo é obrigatório! Ex: A ou O -> A PREFEITURA... O FUNDO... A SECRETARIA..');
      }

      const unformattedCliente = {
        ...formData,
        cnpj: unformattedCnpj,
        telefone: unformatTelefone(formData.telefone),
        fax: unformatCelular(formData.fax),
        logrCep: unformatCEP(formData.logrCep),
      };

      await axios.post(`${API_BASE_URL.CLIENTE}/save`, unformattedCliente, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      setError(
        error.response?.data?.message ||
          error.message ||
          'Erro ao salvar o cliente'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem', minHeight: '2rem', background: 'linear-gradient(90deg, #0b3383, #0e48ad)', color: 'white' }}>
        <Modal.Title style={{ fontSize: '1rem', margin: 0, lineHeight: '1.2', color: 'white' }}>
          <i className="bi bi-building me-2"></i>
          {isEditing ? 'Editar Entidade' : 'Nova Entidade'}
        </Modal.Title>
        <button
          className="btn btn-link text-white p-0 mb-0"
          onClick={onClose}
          title="Fechar"
          style={{ fontSize: '0.9rem', textDecoration: 'none' }}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && (
          <Alert variant="danger" className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Dados básicos */}
          <Row className="g-2 mb-3">
            <Col md={3}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">
                  CNPJ <span className="text-danger">*</span>
                </Form.Label>
                <div className="input-group input-group">
                  <CNPJMaskInput
                    value={formData.cnpj}
                    onChange={handleChange}
                    name="cnpj"
                    readOnly={isEditing}
                    size="sm"
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleBuscarCnpj}
                    disabled={!formData.cnpj || stripMask(formData.cnpj).length !== 14 || isBuscandoCnpj}
                    title="Buscar dados na Receita Federal"
                  >
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </Form.Group>
            </Col>
            <Col md={9}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">
                  Nome <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  size="sm"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">
                  Artigo <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="artigo"
                  value={formData.artigo}
                  onChange={handleChange}
                  size="sm"
                  required
                >
                  <option value="">Selecione...</option>
                  <option value="A">A</option>
                  <option value="O">O</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">E-mail</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">Telefone</Form.Label>
                <TelefoneMaskInput
                  value={formData.telefone}
                  onChange={handleChange}
                  name="telefone"
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">Celular</Form.Label>
                <CelularMaskInput
                  value={formData.fax}
                  onChange={handleChange}
                  name="fax"
                  size="sm"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Endereço */}
          <hr />
          <h6 className="text-muted mb-3">
            <i className="bi bi-geo-alt me-2"></i>
            Endereço
          </h6>

          <Row className="g-2 mb-3">
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">Logradouro</Form.Label>
                <Form.Control
                  type="text"
                  name="logrNome"
                  value={formData.logrNome}
                  onChange={handleChange}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">Número</Form.Label>
                <Form.Control
                  type="text"
                  name="logrNum"
                  value={formData.logrNum}
                  onChange={handleChange}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">Complemento</Form.Label>
                <Form.Control
                  type="text"
                  name="logrCompl"
                  value={formData.logrCompl}
                  onChange={handleChange}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">Bairro</Form.Label>
                <Form.Control
                  type="text"
                  name="logrBairro"
                  value={formData.logrBairro}
                  onChange={handleChange}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">CEP</Form.Label>
                <CEPMaskInput
                  value={formData.logrCep}
                  onChange={handleChange}
                  name="logrCep"
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">UF</Form.Label>
                <Form.Select
                  name="logrUf"
                  value={formData.logrUf}
                  onChange={handleChange}
                  size="sm"
                >
                  <option value="">Selecione...</option>
                  {ufs.map(uf => (
                    <option key={uf.codigo} value={uf.sigla}>
                      {uf.sigla}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1">Cidade</Form.Label>
                <Form.Select
                  name="logrCidade"
                  value={formData.logrCidade}
                  onChange={handleChange}
                  size="sm"
                  disabled={!formData.logrUf}
                >
                  <option value="">Selecione...</option>
                  {cidades.map(cidade => (
                    <option key={cidade.codigo} value={cidade.nome}>
                      {cidade.nome}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          variant="success"
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" />
              Salvando...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1"></i>
              Salvar
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ClienteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  cliente: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ClienteModal;
